// ----------      START   IMPORT  BLOCK        ----------
import type { ReactNode } from 'react';
import { createContext, useContext, useState, useMemo, useCallback } from 'react';
import { UserRole } from '../../Types/User/UserRoles';
import type { UserRead, UserProfilePartialUpdate } from '../../Types/schema-helpers';
import UserService from '../../API/Services/UserService';
// ----------      END   IMPORT  BLOCK        ----------

interface UserContextType {
    user: UserRead | null;
    isLoading: boolean;
    setIsLoading: (loading: boolean) => void;
    hasRole: (role: UserRole | string) => boolean;
    isProfessional: boolean;
    isMedicIntent: boolean;
    isPatientProfileComplete: boolean;
    isMedicProfileComplete: boolean;
    setProfile: (userData: UserRead | ((prev: UserRead | null) => UserRead | null)) => void;
    clearProfile: () => void;
    updateProfileField: <K extends keyof UserRead>(key: K, value: UserRead[K]) => void;
    
    /**
     * Sends partial updates (general_info, contact, addresses, emergency_contacts)
     * to backend via UserService and automatically updates local context state.
     */
    updateUserProfile: (payload: UserProfilePartialUpdate) => Promise<UserRead>;

    education: any[];
    setEducation: (data: any[]) => void;
    userContact: any;
    setUserContact: (data: any) => void;
    whiteList: any[];
    setWhitelist: (data: any[]) => void;
    agenda: any;
    setUserAgenda: (data: any) => void;
}

const UserContext = createContext<UserContextType | null>(null);

export const useUser = () => {
  const context = useContext(UserContext);
  if (context === null) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

export const UserProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<UserRead | null>(null);
  
  // 1. Default to true so guards wait for initial session rehydration
  const [isLoading, setIsLoadingState] = useState<boolean>(true);

  const [education, setEducationState] = useState<any[]>([]);
  const [whiteList, setWhitelistState] = useState<any[]>([]);
  const [userContact, setUserContactState] = useState<any>({});
  const [agenda, setUserAgendaState] = useState<any>({});

  const hasRole = useCallback((role: UserRole | string) => {
    return !!(user?.roles as string[])?.includes(role);
  }, [user]);

  const isProfessional = useMemo(() => hasRole(UserRole.PROFESSIONAL), [hasRole]);

  const isMedicIntent = useMemo(() => {
    const medicRoles: string[] = [
      UserRole.PENDINGPROFESSIONAL, 
      UserRole.VERIFYINGPROFESSIONAL, 
      UserRole.PROFESSIONAL
    ];
    return user?.roles?.some(role => medicRoles.includes(role)) ?? false;
  }, [user]);

  const isPatientProfileComplete = useMemo(() => {
    return !!(user?.questionnaire_status?.patient_profile_complete);
  }, [user]);

  const isMedicProfileComplete = useMemo(() => {
    return !!(user?.questionnaire_status?.medic_profile_complete);
  }, [user]);

  const setIsLoading = useCallback((loading: boolean) => {
    setIsLoadingState(loading);
  }, []);

  // 2. Automatically resolves loading state when profile is set
  const setProfile = useCallback((userData: UserRead | ((prev: UserRead | null) => UserRead | null)) => {
    setUser(userData);
    setIsLoadingState(false);
  }, []);

  // 3. Automatically resolves loading state when cleared
  const clearProfile = useCallback(() => {
    setUser(null);
    setEducationState([]);
    setUserContactState({});
    setWhitelistState([]);
    setUserAgendaState({});
    setIsLoadingState(false);
  }, []);
  
  const updateProfileField = useCallback(<K extends keyof UserRead>(key: K, value: UserRead[K]) => {
    setUser(prevUser => {
        if (!prevUser) return null;
        return {
            ...prevUser,
            [key]: value,
        };
    });
  }, []);

  /**
   * Service wrapper method: Executes API patch request and updates context state in one atomic operation.
   */
  const updateUserProfile = useCallback(async (payload: UserProfilePartialUpdate): Promise<UserRead> => {
    const updatedUser = await UserService.updateUserProfile(payload);
    setUser(updatedUser);
    return updatedUser;
  }, []);

  const setEducation = useCallback((data: any[]) => {
      setEducationState(data);
  }, []);

  const setUserContact = useCallback((data: any) => {
      setUserContactState(data);
  }, []);

  const setWhitelist = useCallback((data: any[]) => {
      setWhitelistState(data);
  }, []);

  const setUserAgenda = useCallback((data: any) => {
      setUserAgendaState(data);
  }, []);

  const value = useMemo(() => ({
    user,
    isLoading,
    setIsLoading,
    hasRole,
    isProfessional,
    isMedicIntent,
    isPatientProfileComplete,
    isMedicProfileComplete,
    setProfile,
    clearProfile,
    updateProfileField,
    updateUserProfile,
    education,
    setEducation,
    userContact,
    setUserContact,
    whiteList, 
    setWhitelist,
    agenda, 
    setUserAgenda,
  }), [
      user,
      isLoading,
      setIsLoading,
      hasRole,
      isProfessional,
      isMedicIntent,
      isPatientProfileComplete,
      isMedicProfileComplete,
      education, 
      userContact, 
      whiteList,
      agenda,
      setProfile, 
      clearProfile, 
      updateProfileField,
      updateUserProfile,
      setEducation,
      setUserContact,
      setWhitelist,
      setUserAgenda,
  ]);

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};